import { PersonOutline } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../../theme";
import type { BookingData } from "../../../../types/view-booking";
import InfoRow from "./InfoRow";
import {
  AccessTime,
  CalendarMonth,
  LocationOn,
  PeopleOutline,
} from "@mui/icons-material";

interface BookingInfoCardProps {
  bookingData?: BookingData | null;
  loading?: boolean;
}

const parseDDMMYYYY = (value?: string) => {
  if (!value) return null;
  const [dd, mm, yyyy] = value.split("-").map((x) => Number(x));
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd)
    return null;
  return d;
};

const formatPrettyDateDDMMYYYY = (value?: string) => {
  const d = parseDDMMYYYY(value);
  if (!d) return "N/A";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
};

const formatCustomPrettyDate = (value?: string) => {
  if (!value || value === "N/A") return "N/A";
  const parts = value.split("-");
  if (parts.length !== 3) return value;

  const [yyyy, mm, dd] = parts.map(Number);
  const d = new Date(yyyy, mm - 1, dd);
  if (isNaN(d.getTime())) return value;

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
};

const formatCleanTime = (timeStr?: string) => {
  if (!timeStr || timeStr === "N/A") return "";
  
  const parts = timeStr.split(":");
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  
  return timeStr.replace(/:00\s/, ' ');
};


const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{ fontWeight: 600, fontSize: 16, color: "text.secondary", mb: 2 }}
  >
    {children}
  </Typography>
);

const DateBlock = ({ label, value }: { label: string; value: string }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: colors.primary[900],
        borderRadius: "10px",
        p: 1.5,
        border: `1px solid ${colors.primary[300]}`,
      }}
    >
      <Typography
        sx={{ fontSize: 10, color: "text.secondary", fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
        {value}
      </Typography>
    </Box>
  );
};

function BookingInfoSkeleton() {
  return (
    <Stack spacing={2}>
      <Skeleton variant="text" width="50%" height={24} />
      <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="circular" width={80} height={80} />
        <Stack spacing={1} flex={1}>
          <Skeleton variant="text" width="70%" height={22} />
          <Skeleton variant="text" width="50%" height={18} />
          <Skeleton variant="text" width="30%" height={18} />
        </Stack>
      </Stack>
      <Skeleton variant="rounded" width="100%" height={60} />
      <Skeleton variant="text" width="80%" height={18} />
      <Skeleton variant="text" width="60%" height={18} />
      <Skeleton variant="text" width="70%" height={18} />
    </Stack>
  );
}

function FuneralView({ bookingData }: { bookingData: BookingData }) {
  const { beloved } = bookingData;
  const details = beloved?.details ?? {};
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dob = details.dateOfBirth;
  const dod = details.dateOfDeath;
  const sunrise = formatPrettyDateDDMMYYYY(dob);
  const sunset = formatPrettyDateDDMMYYYY(dod);

  return (
    <>
      <SectionLabel>Deceased Information</SectionLabel>
      <Box
        sx={{ bgcolor: "background.paper", borderRadius: "12px", p: 2, mb: 2 }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
          <Avatar
            src={beloved?.avatar || undefined}
            alt={beloved?.name || "Deceased"}
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "#cbd5e1",
              fontWeight: 800,
            }}
          >
            <PersonOutline sx={{ fontSize: 48, color: colors.primary[400] }} />
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 18,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {beloved?.name || "N/A"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
              {sunrise !== "N/A" && sunset !== "N/A"
                ? `${dob.replace(/-/g, "/")} - ${dod.replace(/-/g, "/")}`
                : "N/A"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.3 }}>
              {details.gender || "N/A"}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Nickname
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {details.nickname || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Denomination
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {details.denomination || "N/A"}
            </Typography>
          </Box>
        </Stack>
        <Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Religion
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {details.religion || "N/A"}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

function ConferenceView({ bookingData }: { bookingData: BookingData }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const cd = bookingData.conferenceDetails;
  const service = bookingData.services?.[0];
  const title = cd?.conference_title || service?.title || "N/A";
  const startDate = cd?.start_date_of_event || "N/A";
  const endDate = cd?.end_date_of_event;
  const startTime = cd?.start_time || service?.start_time;
  const endTime = cd?.end_time || service?.end_time;
  const venue = cd?.venue_name || service?.venue?.name;
  const city = cd?.city || service?.venue?.city;
  const audience = cd?.expected_audience_size
    ? String(cd.expected_audience_size)
    : undefined;
  const cleanStart = startTime ? formatCleanTime(startTime) : null;
  const cleanEnd = endTime ? formatCleanTime(endTime) : null;
  const timeRange =
    startTime && endTime ? `${cleanStart} – ${cleanEnd}` : (startTime ?? "N/A");

  const isStreamFund =
    bookingData.source_of_booking === "streamfund_application";
  const isDark = theme.palette.mode === "dark";

  const formattedStart = formatCustomPrettyDate(startDate);
  const formattedEnd =
    endDate && endDate !== startDate ? formatCustomPrettyDate(endDate) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 12,
            color: colors.primary[700],
            letterSpacing: "1px",
            textTransform: "uppercase",
          }}
        >
          Conference Details
        </Typography>

        {isStreamFund && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              bgcolor: colors.primary[500],
              color: colors.primary[900],
              px: 1.5,
              py: 0.5,
              borderRadius: "12px",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.5px",
            }}
          >
            STREAMFUND BOOKING
          </Box>
        )}
      </Stack>

      <Box
        sx={{
          bgcolor: isDark ? colors.primary[900] : "#ffffff",
          border: `1px solid ${isDark ? colors.primary[800] : "#e2e8f0"}`,
          borderRadius: "16px",
          p: 3.5,
          boxShadow: isDark
            ? "0 4px 30px rgba(0,0,0,0.2)"
            : "0 10px 30px rgba(15, 23, 42, 0.01)",
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1.5rem",
            color: isDark ? "#ffffff" : colors.primary[100],
            lineHeight: 1.25,
            letterSpacing: "-0.5px",
            mb: 4,
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 4,
            rowGap: 3.5,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <CalendarMonth
              sx={{ color: colors.primary[400], fontSize: 20, mt: 0.3 }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "text.secondary",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                }}
              >
                {formattedEnd ? "DATES" : "START DATE"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: isDark ? colors.primary[300] : colors.primary[100],
                  mt: 0.5,
                }}
              >
                {formattedStart} {formattedEnd ? `– ${formattedEnd}` : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <AccessTime
              sx={{ color: colors.primary[400], fontSize: 20, mt: 0.3 }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "text.secondary",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                }}
              >
                TIME
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: isDark ? colors.primary[300] : colors.primary[100],
                  mt: 0.5,
                }}
              >
                {timeRange}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ gridColumn: { sm: "span 2" } }}>
            <Divider
              sx={{
                borderColor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
              }}
            />
          </Box>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <LocationOn
              sx={{ color: colors.primary[400], fontSize: 20, mt: 0.3 }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "text.secondary",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                }}
              >
                VENUE
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: isDark ? "#ffffff" : colors.primary[100],
                  mt: 0.5,
                }}
              >
                {venue || "N/A"}
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}
              >
                {city || "N/A"}
              </Typography>
            </Box>
          </Stack>

          {audience && (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <PeopleOutline
                sx={{ color: colors.primary[400], fontSize: 20, mt: 0.3 }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.secondary",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                  }}
                >
                  ATTENDANCE
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: isDark ? "#ffffff" : colors.primary[100],
                    mt: 0.5,
                  }}
                >
                  {Number(audience).toLocaleString()} guests
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function ChurchView({ bookingData }: { bookingData: BookingData }) {
  const cd = bookingData.churchDetails;
  const service = bookingData.services?.[0];
  const churchName = cd?.church_name || service?.church_name || "N/A";
  const date = cd?.date_of_service || service?.date_of_service || "N/A";
  const startTime = cd?.start_time || service?.start_time;
  const endTime = cd?.end_time || service?.end_time;
  const preacher = cd?.leader_preacher_name || service?.leader_preacher_name;
  const venue = service?.venue?.name;
  const timeRange =
    startTime && endTime ? `${startTime} – ${endTime}` : (startTime ?? "N/A");

  return (
    <>
      <SectionLabel>Church Service</SectionLabel>
      <Box
        sx={{ bgcolor: "background.paper", borderRadius: "12px", p: 2, mb: 2 }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 18,
            color: "#0f172a",
            lineHeight: 1.2,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {churchName}
        </Typography>
        <Stack direction="row" spacing={2}>
          <DateBlock label="DATE" value={date} />
          <DateBlock label="TIME" value={timeRange} />
        </Stack>
      </Box>
      <Stack spacing={0.5}>
        {preacher && <InfoRow label="Preacher:" value={preacher} />}
        <InfoRow label="Venue:" value={venue || "N/A"} />
      </Stack>
    </>
  );
}

function OtherView({ bookingData }: { bookingData: BookingData }) {
  const cd = bookingData.contentDetails;
  const service = bookingData.services?.[0];
  const title = cd?.title || service?.title || "N/A";
  const date = cd?.date || service?.date_of_service || "N/A";
  const startTime = cd?.start_time || service?.start_time;
  const endTime = cd?.end_time || service?.end_time;
  const host = cd?.host_name;
  const guest = cd?.guest_name;
  const venue = service?.venue?.name;
  const timeRange =
    startTime && endTime ? `${startTime} – ${endTime}` : (startTime ?? "N/A");

  return (
    <>
      <SectionLabel>Event Details</SectionLabel>
      <Box
        sx={{ bgcolor: "background.paper", borderRadius: "12px", p: 2, mb: 2 }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 18,
            color: "#0f172a",
            lineHeight: 1.2,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Stack direction="row" spacing={2}>
          <DateBlock label="DATE" value={date} />
          <DateBlock label="TIME" value={timeRange} />
        </Stack>
      </Box>
      <Stack spacing={0.5}>
        {host && <InfoRow label="Host:" value={host} />}
        {guest && <InfoRow label="Guest:" value={guest} />}
        <InfoRow label="Venue:" value={venue || "N/A"} />
      </Stack>
    </>
  );
}

export default function BookingInfoCard({
  bookingData,
  loading,
}: BookingInfoCardProps) {
  const service = (bookingData?.service ?? "").toLowerCase();

  const renderContent = () => {
    if (loading || !bookingData) return <BookingInfoSkeleton />;
    if (service === "conference")
      return <ConferenceView bookingData={bookingData} />;
    if (service === "church") return <ChurchView bookingData={bookingData} />;
    if (service === "other") return <OtherView bookingData={bookingData} />;
    return <FuneralView bookingData={bookingData} />;
  };

  return (
    <Paper
      sx={{ flex: 1, borderRadius: "12px", p: 2, boxShadow: 0, height: "100%" }}
    >
      {renderContent()}
    </Paper>
  );
}
